import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/icons";
import { waLink } from "@/config/site";
import { track } from "@/lib/analytics";

// Inner-page hero — deep navy with masked headline reveal
export const PageHero = ({ eyebrow, titleLines, copy, ctaLabel = "Get a Free Property Assessment", ctaHref = "/contact", note }) => (
  <section className="relative overflow-hidden bg-navy-900 pb-20 pt-36 sm:pb-24 sm:pt-44">
    <div
      className="pointer-events-none absolute inset-0"
      style={{ background: "radial-gradient(ellipse 60% 50% at 85% 0%, rgba(201,162,39,0.14), transparent 65%)" }}
      aria-hidden="true"
    />
    <div className="container-site relative">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="flex items-center gap-4"
      >
        <span className="gold-rule" aria-hidden="true" />
        <span className="eyebrow">{eyebrow}</span>
      </motion.div>
      <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-6xl">
        {titleLines.map((line, i) => (
          <MaskedLine key={i} delay={0.12 + i * 0.14}>
            {i === titleLines.length - 1 ? <span className="text-gold-400">{line}</span> : line}
          </MaskedLine>
        ))}
      </h1>
      {copy && (
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg"
        >
          {copy}
        </motion.p>
      )}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <Link to={ctaHref} className="btn-gold" data-testid="page-hero-cta" onClick={() => track("assessment_request", { location: "page_hero" })}>
          {ctaLabel} <ArrowRight className="h-4 w-4" />
        </Link>
        <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-ghost-light" data-testid="page-hero-whatsapp" onClick={() => track("whatsapp_click", { location: "page_hero" })}>
          <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> Talk to Us on WhatsApp
        </a>
      </motion.div>
      {note && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-8 text-xs uppercase tracking-[0.22em] text-white/40"
        >
          {note}
        </motion.p>
      )}
    </div>
  </section>
);
